import { createFileRoute, redirect } from "@tanstack/react-router";
import { hasRole } from "@/lib/auth";
import { ChevronRight, ChevronDown } from "lucide-react";
import z from "zod/v4";
import { Gbp } from "@/components/svg/gbp";
import { EU } from "@/components/svg/eu";
import { PayeeDetailsPerCurrency } from "@/types/merchant";
import {
  fetchOptimusBeneficiariesForCustomer,
  OptimusBeneficiariesResponseSchema,
} from "./-components/accounts/fetch-optimus-beneficiaries-request";
import { CardWithHeader } from "@/components/card-with-header";
import { ChangeBeneficiaryModalButton } from "./-components/accounts/change-beneficiary-modal-button";
import { getMerchantQuery, useMerchant } from "@/hooks/use-merchant";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { Fragment } from "react";
import clsx from "clsx";
import { SupportEmailLink } from "@/components/support-email-link";

export const Route = createFileRoute(
  "/(app)/_main-layout/_settings/settings/accounts",
)({
  component: () => <AccountsComponent />,
  beforeLoad({ context }) {
    if (!hasRole("EDIT_SETTINGS", context.authorities)) {
      throw redirect({ to: "/settings/account-settings", search: true });
    }
  },
  loader: async ({
    context: { queryClient, accessToken, merchantId, selectedApplicationId },
  }) => {
    const merchant = await queryClient.ensureQueryData(
      getMerchantQuery({
        accessToken,
        merchantId,
      }),
    );
    const applications = merchant.applications;
    const selectedApplication = applications.find(
      (application) => application.applicationId === selectedApplicationId,
    );

    const payeeDetailsPerCurrency =
      selectedApplication?.payeeDetailsPerCurrency;
    const optimusCustomerIds = [];
    if (payeeDetailsPerCurrency) {
      for (const payee of payeeDetailsPerCurrency) {
        if (payee.vaProviderType === "OPTIMUS" && payee.vaCustomerId) {
          optimusCustomerIds.push(payee.vaCustomerId);
        }
      }
    }

    const beneficiaries = await Promise.all(
      optimusCustomerIds.map((customerId) =>
        fetchOptimusBeneficiariesForCustomer({
          customerId,
          accessToken,
        }),
      ),
    );
    const beneficiariesByCustomerId: BeneficiariesByCustomerId = {};
    for (const beneficiaryArray of beneficiaries) {
      const first = beneficiaryArray.at(0);
      if (!first) {
        continue;
      }
      beneficiariesByCustomerId[first.optimusExternalCustomerId] =
        beneficiaryArray.filter(
          (beneficiary) => beneficiary.status === "Active",
        );
    }

    return {
      beneficiariesByCustomerId,
    };
  },
});

export type BeneficiariesByCustomerId = Record<
  string,
  z.infer<typeof OptimusBeneficiariesResponseSchema>
>;

function AccountsComponent() {
  const merchant = useMerchant();
  const selectedApplication = useSelectedApplication();

  const { beneficiariesByCustomerId } = Route.useLoaderData();

  const payinAccounts = (
    merchant.applications.find(
      (application) =>
        application.applicationId === selectedApplication.applicationId,
    )?.payeeDetailsPerCurrency ?? []
  ).sort((a) => (a.defaultForCurrency ? -1 : 1));

  const allAccounts = Object.groupBy(
    payinAccounts,
    (account) => account.forCurrency,
  );
  const hasNoAccounts =
    allAccounts.GBP?.length === 0 && allAccounts.EUR?.length === 0;

  return (
    <>
      <PayinAccounts
        accounts={allAccounts.GBP ?? []}
        beneficiariesByCustomerId={beneficiariesByCustomerId}
        currency="GBP"
      />
      <PayinAccounts
        accounts={allAccounts.EUR ?? []}
        beneficiariesByCustomerId={beneficiariesByCustomerId}
        currency="EUR"
      />

      {hasNoAccounts ? (
        <div>
          <div className="w-full rounded-t-2xl bg-[#E8EAFF] p-6 font-bold text-volume-text-purple">
            Pay-in Accounts
          </div>
          <div className="p-6 bg-white rounded-b-2xl">
            <p className="text-volume-dark-grey text-sm">
              No pay-in accounts configured for this application.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}
function PayinAccounts({
  currency,
  accounts,
  beneficiariesByCustomerId,
}: {
  currency: "GBP" | "EUR";
  accounts: PayeeDetailsPerCurrency[];
  beneficiariesByCustomerId: BeneficiariesByCustomerId;
}) {
  return (
    <div className=" space-y-8">
      {/* GBP Payin Accounts */}
      {accounts.length > 0 && (
        <div>
          <div className="w-full rounded-t-2xl bg-[#E8EAFF] py-6 p-4 font-bold text-volume-text-purple flex items-center gap-2 border border-gray-200">
            {currency === "GBP" ? <Gbp /> : <EU />} Accounts
          </div>
          <div className="p-6 bg-white rounded-b-2xl border border-gray-200">
            <p className="text-volume-dark-grey text-sm mb-4">
              The following illustrates your flow of funds in the Volume
              platform for {currency} transactions.
            </p>
            {accounts.map((payinAccount, index) => {
              return (
                <Fragment key={payinAccount.id}>
                  {index > 0 && (
                    <div className="my-8 border-t border-gray-200"></div>
                  )}
                  <div
                    className={clsx(
                      "my-2 text-lg font-medium",
                      payinAccount.defaultForCurrency ? "" : "ml-4",
                    )}
                  >
                    {payinAccount.accountHolderName}
                  </div>
                  <PayinAccountCard
                    key={payinAccount.id}
                    payinAccount={payinAccount}
                    beneficiariesByCustomerId={beneficiariesByCustomerId}
                  />
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PayinAccountCard({
  payinAccount,
  beneficiariesByCustomerId,
}: {
  payinAccount: PayeeDetailsPerCurrency;
  beneficiariesByCustomerId: BeneficiariesByCustomerId;
}) {
  const currentBeneficiary =
    payinAccount.vaProviderType === "OPTIMUS" &&
    payinAccount.vaDefaultPayOutBeneficiaryAccountId
      ? beneficiariesByCustomerId[payinAccount.vaCustomerId ?? ""]?.find(
          (b) =>
            b.optimusBeneficiaryId ===
            payinAccount.vaDefaultPayOutBeneficiaryAccountId,
        )
      : null;
  return (
    <div
      className={`grid grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-12 pl-4 pr-4 relative pb-8 ${payinAccount.defaultForCurrency ? "bg-green-50 border border-green-200 rounded-2xl px-4 pt-16 pb-4" : ""}`}
    >
      {payinAccount.defaultForCurrency && (
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-green-100 px-3 py-2 rounded-xl w-fit">
          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
          <span className="text-green-800 text-sm font-medium">
            Active Flow of Funds
          </span>
        </div>
      )}
      <div className="h-full">
        <CardWithHeader title="Payers" subtitle="Customer Payments">
          <p className="text-volume-dark-grey text-sm">
            Your customers send make Payments using the Volume SDK in your App
            or Website.
          </p>
        </CardWithHeader>
      </div>
      <div className="hidden xl:flex items-center justify-center absolute left-1/3 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <ChevronRight className="w-12 h-12 text-volume-text-purple" />
      </div>
      <div className="flex xl:hidden items-center justify-center">
        <ChevronDown className="w-8 h-8 text-volume-text-purple" />
      </div>
      <div className="h-full">
        {payinAccount.vaProviderType === "NONE" ? (
          <NonVaInfoAccountCard />
        ) : (
          <CardWithHeader title="Virtual Account" subtitle="Fund Safeguarding">
            <div className="flex flex-col h-full">
              <p className="text-volume-dark-grey text-sm">
                This dedicated Virtual Account will receive and safeguard funds
                from your customers until you pay out to your designated
                destination account.
              </p>
              <BankDetailsSection
                accountIdentification1={payinAccount.accountIdentification1}
                accountIdentification2={payinAccount.accountIdentification2}
                accountHolderName={payinAccount.accountHolderName}
              />

              {payinAccount.vaCustomerId &&
                Object.keys(beneficiariesByCustomerId).find(
                  (key) => key === payinAccount.vaCustomerId,
                ) && <div className="mt-4 flex-1"></div>}
            </div>
          </CardWithHeader>
        )}
      </div>
      {payinAccount.vaProviderType !== "NONE" && (
        <>
          <div className="hidden xl:flex items-center justify-center absolute left-2/3 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <ChevronRight className="w-12 h-12 text-volume-text-purple" />
          </div>
          <div className="flex xl:hidden items-center justify-center">
            <ChevronDown className="w-8 h-8 text-volume-text-purple" />
          </div>
          <div className="h-full">
            <DestinationAccountCard
              payinAccount={payinAccount}
              beneficiariesByCustomerId={beneficiariesByCustomerId}
              currentBeneficiary={currentBeneficiary}
            />
          </div>
        </>
      )}
      {payinAccount.vaProviderType === "NONE" && (
        <>
          <div className="hidden xl:flex items-center justify-center absolute left-2/3 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <ChevronRight className="w-12 h-12 text-volume-text-purple" />
          </div>
          <div className="flex xl:hidden items-center justify-center">
            <ChevronDown className="w-8 h-8 text-volume-text-purple" />
          </div>
          <NonVaDestinationAccountCard payinAccount={payinAccount} />
        </>
      )}
    </div>
  );
}

function NonVaDestinationAccountCard({
  payinAccount,
}: {
  payinAccount: PayeeDetailsPerCurrency;
}) {
  return (
    <CardWithHeader title="Destination Account">
      <div className="flex flex-col ">
        <p className="text-volume-dark-grey text-sm mb-auto">
          This account is where your funds from transactions will be sent. To
          change this account please contact <SupportEmailLink />.
        </p>
        <div className="mt-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">
              Account Number:
            </span>
            <span className="text-V_Black text-sm font-medium">
              {payinAccount.accountIdentification1.number}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">Sort Code:</span>
            <span className="text-V_Black text-sm font-medium">
              {payinAccount.accountIdentification2?.number}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">Account Name:</span>
            <span className="text-V_Black text-sm font-medium">
              {payinAccount.accountHolderName}
            </span>
          </div>
        </div>
      </div>
    </CardWithHeader>
  );
}
function DestinationAccountCard({
  payinAccount,
  beneficiariesByCustomerId,
  currentBeneficiary,
}: {
  payinAccount: PayeeDetailsPerCurrency;
  beneficiariesByCustomerId: BeneficiariesByCustomerId;
  currentBeneficiary:
    | BeneficiariesByCustomerId[string][number]
    | null
    | undefined;
}) {
  if (
    !(
      currentBeneficiary &&
      currentBeneficiary.accountNumber &&
      currentBeneficiary.sortCode
    )
  )
    return <DestinationAccountDetailsNotAvailable />;
  if (!payinAccount.vaCustomerId)
    return <DestinationAccountDetailsNotAvailable />;
  const destinationAccount =
    beneficiariesByCustomerId[payinAccount.vaCustomerId];
  if (!destinationAccount) return <DestinationAccountDetailsNotAvailable />;

  const accountNumber = destinationAccount.find(
    (b) =>
      b.optimusBeneficiaryId ===
      payinAccount.vaDefaultPayOutBeneficiaryAccountId,
  )?.accountNumber;
  const sortCode = destinationAccount.find(
    (b) =>
      b.optimusBeneficiaryId ===
      payinAccount.vaDefaultPayOutBeneficiaryAccountId,
  )?.sortCode;
  const firstNames = destinationAccount.find(
    (b) =>
      b.optimusBeneficiaryId ===
      payinAccount.vaDefaultPayOutBeneficiaryAccountId,
  )?.firstNames;

  if (!accountNumber || !sortCode || !firstNames)
    return <DestinationAccountDetailsNotAvailable />;

  return (
    <CardWithHeader
      title="Destination Account"
      subtitle="Destination Payout Account"
    >
      <div className="flex flex-col h-full">
        <p className="text-volume-dark-grey text-sm mb-auto">
          This account is where your safeguarded funds will be sent, either by
          your scheduled payouts or by manually initiated payouts.
        </p>
        <div className="mt-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">
              Account Number:
            </span>
            <span className="text-V_Black text-sm font-medium">
              {accountNumber}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">Sort Code:</span>
            <span className="text-V_Black text-sm font-medium">{sortCode}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-volume-dark-grey text-sm">Account Name:</span>
            <span className="text-V_Black text-sm font-medium">
              {firstNames}
            </span>
          </div>
        </div>

        <ChangeBeneficiaryModalButton
          payinAccountId={payinAccount.id}
          currentBeneficiaryDetails={{
            accountNumber: currentBeneficiary.accountNumber,
            sortCode: currentBeneficiary.sortCode,
            firstNames: currentBeneficiary.firstNames,
            id: currentBeneficiary.optimusBeneficiaryId,
          }}
        />
      </div>
    </CardWithHeader>
  );
}

function DestinationAccountDetailsNotAvailable() {
  return (
    <CardWithHeader
      title="Destination Account"
      subtitle="Destination Payout Account"
    >
      <div className="flex flex-col h-full">
        <p className="text-volume-dark-grey text-sm mb-auto">
          This account is where your safeguarded funds will be sent, either by
          your scheduled payouts or by manually initiated payouts.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg
                className="w-3 h-3 text-yellow-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                Destination account details not available
              </h4>
              <p className="text-sm text-yellow-700">
                The destination account information is not currently available
                for this account. Please contact{" "}
                <a
                  href="mailto:support@getvolume.com"
                  className="text-blue-500 underline"
                >
                  support@getvolume.com
                </a>{" "}
                if you need to update your destination account details.{" "}
              </p>
            </div>
          </div>
        </div>
      </div>
    </CardWithHeader>
  );
}

function BankDetailsSection({
  accountIdentification1,
  accountIdentification2,
  accountHolderName,
}: {
  accountIdentification1: {
    type: string;
    number: string;
  };
  accountIdentification2: {
    type: string;
    number: string;
  } | null;
  accountHolderName?: string;
}) {
  return (
    <div className="mt-4 space-y-2">
      <div className="flex justify-between">
        <span className="text-volume-dark-grey text-sm">
          {accountIdentification2 ? "Account Number" : "IBAN"}
        </span>
        <span className="text-V_Black text-sm font-medium">
          {accountIdentification1?.number}
        </span>
      </div>
      {accountIdentification2 && (
        <div className="flex justify-between">
          <span className="text-volume-dark-grey text-sm">Sort Code:</span>
          <span className="text-V_Black text-sm font-medium">
            {accountIdentification2?.number}
          </span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-volume-dark-grey text-sm">Account Name:</span>
        <span className="text-V_Black text-sm font-medium">
          {accountHolderName}
        </span>
      </div>
    </div>
  );
}

function NonVaInfoAccountCard() {
  return (
    <div className="flex flex-col h-full">
      <div className="w-full rounded-t-2xl bg-[#E8EAFF] p-4 font-bold text-volume-text-purple">
        Virtual Account
      </div>
      <div className="p-4 bg-white rounded-b-2xl border border-gray-200 flex-1 flex flex-col">
        <p className="text-volume-dark-grey text-sm text-center mb-4">
          This dedicated virtual IBAN will receive and safeguard funds from your
          customers until you pay out to your designated destination account.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-auto">
          <p className="text-volume-dark-grey text-sm">
            You don't currently have a Virtual Account, and funds will be sent
            directly from payers to your Destination Account. If you are
            interested in a Virtual Account please{" "}
            <a
              href="mailto:support@getvolume.com"
              className="text-volume-text-purple underline hover:text-volume-text-purple/80"
            >
              contact us
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
